import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface SortableOptionListProps {
  ids: readonly string[];
  axis: 'x' | 'y';
  /** The name a screen reader hears for an item. */
  nameOf: (id: string) => string;
  onMove: (from: number, to: number) => void;
  children: ReactNode;
}

/**
 * One reorderable group: the options of a card, or the values of one option. Each group has its own
 * context, so a value can never land in another group. The pointer drags; the keyboard goes through
 * `useSortableOption`'s arrow keys, one step at a time, instead of dnd-kit's pick-up-and-drop.
 */
export function SortableOptionList({
  ids,
  axis,
  nameOf,
  onMove,
  children,
}: SortableOptionListProps) {
  const { t } = useTranslation('products');
  const sensors = useSensors(
    // A few pixels of movement before a drag starts, so a click on a chip stays a click.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Touch waits for a press so the page still scrolls.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );
  const position = (id: UniqueIdentifier) => ids.indexOf(String(id)) + 1;
  const announce = (key: 'picked' | 'over' | 'dropped', id: UniqueIdentifier, at = id) =>
    t(`optionsCard.dnd.${key}`, {
      name: nameOf(String(id)),
      position: position(at),
      count: ids.length,
    });

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from >= 0 && to >= 0) onMove(from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => announce('picked', active.id),
          onDragOver: ({ active, over }) =>
            over ? announce('over', active.id, over.id) : undefined,
          onDragEnd: ({ active, over }) =>
            announce('dropped', active.id, over ? over.id : active.id),
          onDragCancel: ({ active }) =>
            t('optionsCard.dnd.cancelled', { name: nameOf(String(active.id)) }),
        },
        screenReaderInstructions: { draggable: t(`optionsCard.dnd.instructions.${axis}`) },
      }}
    >
      <SortableContext
        items={[...ids]}
        strategy={axis === 'y' ? verticalListSortingStrategy : rectSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
}
